"use client"

import React, {useMemo, useRef, useState, useEffect} from "react";
import dynamic from "next/dynamic";
import {Input} from "@/components/ui/input"
import {Button} from "@/components/ui/button"
import {Badge} from "@/components/ui/badge"
import {Bug, Check, Clock, Paperclip, Lock, MailX} from "lucide-react";
import {
  TwitterShareButton,
  LinkedinShareButton,
  TelegramShareButton,
  EmailShareButton,
  WhatsappShareButton,
  TwitterIcon,
  LinkedinIcon,
  TelegramIcon,
  EmailIcon,
  WhatsappIcon,
} from 'next-share';
import { calculateFileHashes } from "@/lib/utils";
import { copyToClipboard } from "@/lib/utils";
import { useAnimation } from "@/lib/animation-context";
import { buildAbsoluteUrl } from "@/lib/site-config";
import { buildLocalizedPath } from "@/lib/i18n";
import { useTranslations } from "@/components/i18n/use-translations";
import ScanShowcase from "@/components/ui/scan-showcase";

// Bar positions (percent) where each scan step starts: Submitted, Enriching, Correlating, Results
const PROGRESS_STEP_THRESHOLDS = [0, 33, 66, 100];

// Lazy load LiveFeed component for better initial load performance
const LiveFeed = dynamic(() => import("@/components/ui/live-feed"), {
    ssr: false,
    loading: () => <div className="h-10" />
});

// Type declaration for window with file hashes
declare global {
  interface Window {
    __fileHashes?: { md5: string; sha1: string; sha256: string };
  }
}

type FeedStatusSummary = { name: string; status: string; message?: string; type?: string; matched?: boolean };
type FeedSummary = { statuses: FeedStatusSummary[] };
type MatchSummary = { feed: string; source?: string; indicator?: string; indicatorType?: string };
type AlternativeIndicator = { label: string; indicators: Array<{ value: string; tagged: boolean }> };
type FeedStatusInfo = { classes: string; icon: "check" | "alert" };

function isMatchSummary(value: unknown): value is MatchSummary {
    if (!value || typeof value !== "object") {
        return false;
    }

    const record = value as Record<string, unknown>;

    if (typeof record.feed !== "string") {
        return false;
    }

    if (record.source !== undefined && typeof record.source !== "string") {
        return false;
    }

    if (record.indicator !== undefined && typeof record.indicator !== "string") {
        return false;
    }

    if (record.indicatorType !== undefined && typeof record.indicatorType !== "string") {
        return false;
    }

    return true;
}

function isAlternativeIndicator(value: unknown): value is AlternativeIndicator {
    if (!value || typeof value !== "object") {
        return false;
    }

    const record = value as Record<string, unknown>;

    if (typeof record.label !== "string") {
        return false;
    }

    if (!Array.isArray(record.indicators)) {
        return false;
    }

    return record.indicators.every((item: unknown) => 
        item && typeof item === "object" &&
        typeof (item as Record<string, unknown>).value === "string" &&
        typeof (item as Record<string, unknown>).tagged === "boolean"
    );
}

function isFeedStatusSummary(value: unknown): value is FeedStatusSummary {
    if (!value || typeof value !== "object") {
        return false;
    }

    const record = value as Record<string, unknown>;

    if (typeof record.name !== "string" || typeof record.status !== "string") {
        return false;
    }

    if (record.message !== undefined && typeof record.message !== "string") {
        return false;
    }

    return true;
}

export default function Home() {
    const { lang, messages } = useTranslations();

    const [scanResult, setScanResult] = useState<boolean | null>(null); // Allowing boolean or null
    const [scanType, setScanType] = useState<string | null>(null); // Scan type
    const [scanStatus, setScanStatus] = useState<"success" | "error" | null>(null);
    const [totalProcessingTime, setTotalProcessingTime] = useState<number | null>(null);  // State to store scan time in milliseconds
    const [matches, setMatches] = useState<MatchSummary[]>([]);
    const [feedSummary, setFeedSummary] = useState<FeedSummary | null>(null);
    const [showAllSources, setShowAllSources] = useState(false);
    const [progress, setProgress] = useState(0); // Progress percentage
    const [isScanning, setIsScanning] = useState(false); // Tracks if a scan is active
    const [inputValue, setInputValue] = useState(""); // Tracks input value
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isBusy, setIsBusy] = useState(false); // Free tier throttled (429): show the friendly "high demand" card
    const [normalizedIndicator, setNormalizedIndicator] = useState<string | null>(null);
    const [originalTagged, setOriginalTagged] = useState<boolean>(false);
    const [alternativeIndicators, setAlternativeIndicators] = useState<AlternativeIndicator[]>([]);
    const [mxMissing, setMxMissing] = useState<string[]>([]);
    
    const { startScanning, startPhishing, stopAnimation } = useAnimation();

    // Check for scan query parameter on mount
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const params = new URLSearchParams(window.location.search);
            const scanParam = params.get('scan');
            if (scanParam) {
                setInputValue(decodeURIComponent(scanParam));
            }
        }
    }, []);

    const progressFrameRef = useRef<number | null>(null);
    const progressStartRef = useRef<number>(0);
    const progressValueRef = useRef(0);

    const updateProgress = (value: number) => {
        progressValueRef.current = value;
        setProgress(value);
    };

    const cancelProgressAnimation = () => {
        if (progressFrameRef.current !== null) {
            cancelAnimationFrame(progressFrameRef.current);
            progressFrameRef.current = null;
        }
    };

    const startProgressAnimation = () => {
        cancelProgressAnimation();
        progressStartRef.current = performance.now();
        progressValueRef.current = 0;
        updateProgress(0);

        const tick = () => {
            const elapsed = performance.now() - progressStartRef.current;
            const dynamicDuration = Math.max(3200, elapsed + 1500);
            const target = Math.min(95, (elapsed / dynamicDuration) * 95);

            if (target > progressValueRef.current) {
                updateProgress(target);
            }

            progressFrameRef.current = requestAnimationFrame(tick);
        };

        progressFrameRef.current = requestAnimationFrame(tick);
    };

    const finishProgressAnimation = (target: number = 100, duration: number = 450) => {
        return new Promise<void>((resolve) => {
            cancelProgressAnimation();

            const startingValue = progressValueRef.current;
            const start = performance.now();
            // Fast responses still walk through every remaining step instead of jumping straight to "Results"
            const remainingSteps = PROGRESS_STEP_THRESHOLDS.filter((threshold) => threshold > startingValue).length;
            const totalDuration = Math.max(duration, remainingSteps * 260);

            const animate = () => {
                const elapsed = performance.now() - start;
                const ratio = Math.min(elapsed / totalDuration, 1);
                const eased = ratio < 0.5 ? 2 * ratio * ratio : 1 - Math.pow(-2 * ratio + 2, 2) / 2;
                const value = startingValue + (target - startingValue) * eased;

                updateProgress(value);

                if (ratio < 1) {
                    progressFrameRef.current = requestAnimationFrame(animate);
                } else {
                    progressFrameRef.current = null;
                    resolve();
                }
            };

            progressFrameRef.current = requestAnimationFrame(animate);
        });
    };

    // Removed unused stopProgressAnimation to fix ESLint error

    // Use animation helpers from context: startScanning(), startPhishing(), stopAnimation()

    const handleCopy = async (text: string) => {
        try {
            await copyToClipboard(text);
        } catch (err) {
            console.error('copy failed', err);
        }
    };
    
    const handleInputChange = (event: { target: { value: string }; }) => {
        const newValue = event.target.value.trim();
        setInputValue(newValue); // Update input value on change
        
        // Clear file hashes if input is manually changed
        if (window.__fileHashes && newValue !== window.__fileHashes.sha256) {
            delete window.__fileHashes;
        }
    };

    // Handle the Enter key press specifically on the input field
    const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();  // Prevent default action (form submission, etc.)
            await handleScanClick();  // Trigger the scan and wait for the result
        }
    };

    const handleScanClick = async () => {
        if (isScanning) {
            return;
        }

        if (!inputValue) {
            setErrorMessage("Please provide an Indicator of Compromise before starting the scan.");
            return;
        }

        // Validate input length (2048 chars max - supports very long URLs)
        if (inputValue.length > 2048) {
            setErrorMessage("Input exceeds maximum length of 2048 characters. Please shorten your indicator.");
            return;
        }

        const fileHashes = window.__fileHashes || null;
        const hashIndicators = fileHashes
            ? [fileHashes.md5, fileHashes.sha1, fileHashes.sha256].filter(Boolean)
            : [];

        const isValidIOC =
            hashIndicators.length > 0 ||
            /^https?:\/\/\S+$/i.test(inputValue) ||
            /^\d{1,3}(\.\d{1,3}){3}$/.test(inputValue) ||
            /^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$/.test(inputValue) ||
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inputValue) ||
            /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(inputValue) ||
            /^[a-fA-F0-9]{64}$/i.test(inputValue) ||
            /^[a-fA-F0-9]{32}$/i.test(inputValue) ||
            /^[a-fA-F0-9]{40}$/i.test(inputValue);

        if (!isValidIOC) {
            setErrorMessage("We couldn't recognize that indicator. Supported formats include full URLs (https://example.com), IPv4/IPv6 addresses, domains, email addresses, and file hashes (MD5/SHA1/SHA256).");
            return;
        }

        try {
            resetValues();
            setIsScanning(true); // Mark scanning as active
            setScanStatus(null);
            setErrorMessage(null);
            startProgressAnimation();
            startScanning();

            // Make request to the backend handler
            const requestBody = hashIndicators.length
                ? { input: inputValue, iocs: hashIndicators }
                : { input: inputValue };

            const response = await fetch("/.netlify/functions/scanHandler", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(requestBody),
            });

            if (response.status === 429) {
                setIsBusy(true);
                throw new Error(messages.home.busy.body);
            }

            if (!response.ok) {
                let errorMessage = "Our servers are currently busy processing licensed requests. Please try again shortly.";

                try {
                    const errorData = await response.json();

                    if (errorData?.error) {
                        errorMessage = errorData.error;
                    }
                } catch {
                    // Ignore JSON parsing errors and use the default message.
                }

                throw new Error(errorMessage);
            }

            const data: Record<string, unknown> = await response.json();

            const {
                result: resultValue,
                type: typeValue,
                total_processing_time: totalProcessingTimeValue,
                matches: matchesValue,
                feed_summary: feedSummaryValue,
                normalized_indicator: normalizedIndicatorValue,
                original_tagged: originalTaggedValue,
                alternative_indicators: alternativeIndicatorsValue,
                mx_missing: mxMissingValue
            } = data;

            const matchList: MatchSummary[] = Array.isArray(matchesValue) ? matchesValue.filter(isMatchSummary).map((match) => ({ 
                feed: match.feed, 
                source: match.source && match.source.length ? match.source : undefined,
                indicator: match.indicator,
                indicatorType: match.indicatorType
            })) : [];

            const alternativeList: AlternativeIndicator[] = Array.isArray(alternativeIndicatorsValue) ? 
                alternativeIndicatorsValue.filter(isAlternativeIndicator) : [];

            let summary: FeedSummary | null = null;
            
            // Map feedSummaryValue.statuses, preserving 'matched' property from backend
            if (feedSummaryValue && typeof feedSummaryValue === "object") {
                const feedSummaryRecord = feedSummaryValue as Record<string, unknown>;
                const statusesRaw = feedSummaryRecord.statuses;
                const statuses = Array.isArray(statusesRaw)
                    ? statusesRaw.filter(isFeedStatusSummary).map((feed: FeedStatusSummary) => ({
                        name: feed.name,
                        status: feed.status,
                        message: feed.message && feed.message.length ? feed.message : undefined,
                        matched: typeof feed.matched === 'boolean' ? feed.matched : false
                    }))
                    : [];
                summary = { statuses };
            }

            const hasResult = typeof resultValue === "boolean";
            const isMalicious = hasResult ? resultValue : false;
            const indicatorType = typeof typeValue === "string" ? typeValue : null;
            const processingTime = typeof totalProcessingTimeValue === "number" ? totalProcessingTimeValue : null;
            const normalizedIoc = typeof normalizedIndicatorValue === "string" ? normalizedIndicatorValue : null;
            const wasOriginalTagged = typeof originalTaggedValue === "boolean" ? originalTaggedValue : false;

            setScanResult(hasResult ? resultValue : null);
            setScanType(indicatorType);
            setTotalProcessingTime(processingTime);
            setMatches(matchList);
            setFeedSummary(summary);
            setNormalizedIndicator(normalizedIoc);
            setOriginalTagged(wasOriginalTagged);
            setAlternativeIndicators(alternativeList);
            setMxMissing(Array.isArray(mxMissingValue) ? mxMissingValue.filter((value): value is string => typeof value === "string") : []);

            await finishProgressAnimation();
            
            // Trigger phishing animation if malicious
            if (isMalicious) {
                startPhishing();
            } else {
                stopAnimation();
            }
            
            setScanStatus("success");
        } catch (error) {
            const message = error instanceof Error ? error.message : "An error occurred during the scanning process. Please try again.";
            setErrorMessage(message);
            await finishProgressAnimation(100, 350);
            stopAnimation();
            setScanStatus("error");
        } finally {
            cancelProgressAnimation();
            setIsScanning(false);
        }
    };

    function formatDuration(durationInMs: number | null): string {
        if (durationInMs === null || Number.isNaN(durationInMs) || durationInMs < 0) {
            return "-";
        }

        if (durationInMs < 1000) {
            return `${durationInMs}ms`;
        }

        const units: Array<[number, string]> = [
            [1000 * 60 * 60 * 24, "day"],
            [1000 * 60 * 60, "hour"],
            [1000 * 60, "minute"],
            [1000, "second"],
        ];

        let remaining = durationInMs;
        const parts: string[] = [];

        for (const [unitMs, label] of units) {
            if (remaining >= unitMs) {
                const value = Math.floor(remaining / unitMs);
                remaining -= value * unitMs;
                parts.push(`${value} ${label}${value !== 1 ? "s" : ""}`);
            }
        }

        if (!parts.length && remaining > 0) {
            const seconds = remaining / 1000;
            const rounded = seconds >= 1 ? seconds.toFixed(seconds >= 10 ? 0 : 1) : seconds.toFixed(2);
            parts.push(`${rounded}s`);
        }

        return parts.length ? parts.join(", ") : "0ms";
    }

    function resetValues() {
        setScanResult(null); // Reset the result
        setScanType(null); // Resets the scan type
        setScanStatus(null);
        setTotalProcessingTime(null); // Reset scan time
        setMatches([]);
        setFeedSummary(null);
        setErrorMessage(null);
        setIsBusy(false);
        setNormalizedIndicator(null);
        setOriginalTagged(false);
        setAlternativeIndicators([]);
        setMxMissing([]);
        cancelProgressAnimation();
        progressStartRef.current = performance.now();
        progressValueRef.current = 0;
        updateProgress(0);
    }

    const formattedProcessingTime = useMemo(() => formatDuration(totalProcessingTime), [totalProcessingTime]);
    const flaggedCount = useMemo(() => feedSummary ? feedSummary.statuses.filter((feed) => feed.matched).length : 0, [feedSummary]);


    const stepLabels = messages.home.steps;
    // A step becomes current once the bar reaches its marker (same positions the labels are drawn at)
    const getProgressStep = (pct: number): { label: string; step: number } => {
        let stepIndex = 0;
        PROGRESS_STEP_THRESHOLDS.forEach((threshold, index) => {
            if (pct >= threshold - 0.5) {
                stepIndex = index;
            }
        });
        return { label: stepLabels[stepIndex], step: stepIndex + 1 };
    };

    const currentStep = getProgressStep(progress);
    // Once a scan starts, the hero collapses so the page focuses on the scan and its results
    const isResultsMode = isScanning || scanStatus !== null;

    const focusScanInput = () => {
        const input = document.getElementById("ioc-input");
        input?.scrollIntoView({ behavior: "smooth", block: "center" });
        input?.focus({ preventScroll: true });
    };

    // Feed status color/icon helper
    function getFeedStatusStyles(status: string, isMatched: boolean): FeedStatusInfo {
        // Only green (no match) or red (match)
        if (isMatched) {
            return { classes: "border-red-600/50 bg-red-600/10 text-red-500", icon: "alert" };
        }
        return { classes: "border-green-600/50 bg-green-600/10 text-green-500", icon: "check" };
    }

    return (
        <div className="flex flex-col min-h-screen w-full">
            {/* Live Feed (recent scans by other users): floating toast, takes no layout space */}
            <LiveFeed />
            {/* Full Page Container */}
            <div className="w-full flex-1 flex flex-col items-center justify-start pt-0 sm:pt-2 px-4 transition duration-0 transform-gpu ease-smooth-submit" style={{ willChange: 'transform, opacity' }}>
                <div className={`w-full max-w-[760px] flex flex-col transition-[gap] duration-500 ease-smooth-submit ${isResultsMode ? 'gap-6' : 'gap-8'}`}>
                    {/* Hero: value prop is the page h1 (the brand lives in the navbar); collapses during scan and results */}
                    <header
                        className={`grid w-full text-center transition-[grid-template-rows,opacity] duration-500 ease-smooth-submit ${isResultsMode ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'}`}
                        aria-hidden={isResultsMode}
                    >
                        <div className="overflow-hidden flex flex-col items-center gap-3">
                            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-white text-balance">
                                {messages.home.heroSubtitle}
                            </h1>
                            <p className="text-sm md:text-base text-gray-300 leading-relaxed max-w-2xl text-pretty">
                                {messages.home.heroDescription}
                            </p>
                        </div>
                    </header>

                    {/* IOC Input Section */}
                    <section id="scan" className="flex w-full flex-col gap-3">
                        <div className="flex w-full flex-col gap-3 rounded-2xl border border-cyan-700/40 bg-linear-to-br from-slate-900/50 to-slate-800/50 backdrop-blur-xs p-5 shadow-[0_0_24px_-4px_rgba(0,255,255,0.15)] hover:shadow-[0_0_32px_-4px_rgba(0,255,255,0.25)] transition-shadow transition-transform duration-700 hover:border-cyan-500/30 focus-within:border-cyan-500/30 focus-within:ring-2 focus-within:ring-cyan-500/20 focus-within:ring-inset ease-smooth-submit">
                            <div className="flex flex-col gap-1">
                                <div className="flex items-center justify-between">
                                    <label htmlFor="ioc-input" className="text-sm font-bold tracking-tight text-slate-100">
                                        {messages.home.indicatorLabel}
                                    </label>
                                </div>
                                <p id="ioc-description" className="text-xs text-gray-400">
                                    {messages.home.indicatorHelp}
                                </p>
                            </div>
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                            <div className="relative flex-1">
                                <Input
                                    id="ioc-input"
                                    type="text"
                                    placeholder={messages.home.inputPlaceholder}
                                    value={inputValue}
                                    onChange={handleInputChange}
                                    onKeyDown={handleKeyDown}
                                    required={true}
                                    autoFocus={true}
                                    aria-label={messages.home.indicatorLabel}
                                    aria-describedby="ioc-description"
                                    autoComplete="off"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    spellCheck={false}
                                    enterKeyHint="search"
                                    className="h-14 pr-16 ring-2 ring-cyan-500/30 focus:ring-cyan-500/60 transition-all text-slate-100 placeholder:text-gray-500 bg-transparent"
                                    onInput={(e) => {
                                        e.currentTarget.value = e.currentTarget.value.replace(/\s/g, '');
                                    }}
                                />
                                {inputValue ? (() => {
                                    let type = window.__fileHashes ? "File" : "";

                                    if (!type) {
                                        if (/^https?:\/\//i.test(inputValue)) {
                                            type = "URL";
                                        } else if (/^\d{1,3}(\.\d{1,3}){3}$/.test(inputValue)) {
                                            type = "IPv4";
                                        } else if (/^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$/.test(inputValue)) {
                                            type = "IPv6";
                                        } else if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inputValue)) {
                                            type = "Email";
                                        } else if (/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(inputValue)) {
                                            type = "Domain";
                                        } else if (/^[a-fA-F0-9]{64}$/i.test(inputValue)) {
                                            type = "SHA256";
                                        } else if (/^[a-fA-F0-9]{32}$/i.test(inputValue)) {
                                            type = "MD5";
                                        } else if (/^[a-fA-F0-9]{40}$/i.test(inputValue)) {
                                            type = "SHA1";
                                        }
                                    }

                                    return type ? (
                                        <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs uppercase tracking-[0.12em] text-muted-foreground">
                                            {type}
                                        </div>
                                    ) : null;
                                })() : (
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                        <input
                                            type="file"
                                            title={messages.home.uploadLabel}
                                            id="file-upload"
                                            className="hidden"
                                            onChange={async (e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    try {
                                                        const buffer = await file.arrayBuffer();
                                                        const hashes = await calculateFileHashes(buffer);
                                                        
                                                        // Store file hashes for later use
                                                        window.__fileHashes = hashes;
                                                        
                                                        // Set file name as the main input value for display
                                                        setInputValue(file.name);
                                                    } catch (err) {
                                                        console.error(err);
                                                    }
                                                }
                                            }}
                                        />
                                        <label
                                            htmlFor="file-upload"
                                            title={messages.home.uploadLabel}
                                            className="group cursor-pointer inline-flex items-center justify-center p-2 text-gray-400 hover:text-cyan-400 transition-colors hover:bg-slate-700/40 rounded-lg"
                                        >
                                            <Paperclip className="w-5 h-5 text-current transition-colors group-hover:text-cyan-400" />
                                        </label>
                                    </div>
                                )}
                            </div>
                        <Button
                            id="scan-action-button"
                            onClick={handleScanClick}
                            aria-busy={isScanning}
                            aria-label={isScanning ? `${messages.home.scanningLabel}... ${currentStep.label}` : messages.home.scanActionLabel}
                            className="h-14 px-8 text-sm font-semibold relative overflow-hidden bg-linear-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white shadow-lg shadow-cyan-500/40 hover:shadow-cyan-500/60 transition-all duration-200 ease-out hover:scale-[1.02] hover:shadow-[0_0_15px_rgba(34,211,238,0.5)] focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-slate-950 disabled:opacity-75"
                            disabled={isScanning}
                        >
                            <span className={`text-center ${isScanning ? 'animate-pulse' : ''}`}>
                                {messages.home.scanButton}
                            </span>
                        </Button>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 text-[0.65rem] sm:text-xs text-slate-500">
                        <p className="flex items-center gap-2">
                            <Lock className="w-4 h-4 text-slate-400" />
                            <a href={buildLocalizedPath("/terms", lang)} className="text-cyan-400 hover:text-cyan-300 transition-colors underline underline-offset-4">{messages.home.termsNotice}</a>
                        </p>
                    </div>
                </div>
            </section>

            {/* Progress Bar Section - Visible during scan and after */}
            {(isScanning || scanStatus) && (
                <section className="w-full" aria-live="polite" aria-label="Scan progress">
                    <div className="w-full max-w-[760px] mx-auto flex flex-col gap-3">
                        {/* Progress Bar */}
                        <div className="w-full bg-slate-800/60 rounded-full h-2 overflow-hidden border border-cyan-700/30">
                            <div 
                                className={`h-full transition-colors duration-300 ${
                                    scanStatus === "error" 
                                        ? "bg-linear-to-r from-red-500 to-red-600" 
                                        : "bg-linear-to-r from-cyan-500 to-blue-600"
                                }`}
                                style={{ width: `${progress}%` }}
                                role="progressbar"
                                aria-valuenow={Math.round(progress)}
                                aria-valuemin={0}
                                aria-valuemax={100}
                            />
                        </div>
                        
                        {/* Step Labels - each label sits at the bar position where its step starts */}
                        <div className="relative h-5 text-xs font-medium">
                            {messages.home.steps.map((step, idx) => {
                                const isFirst = idx === 0;
                                const isLast = idx === messages.home.steps.length - 1;
                                const isDone = currentStep.step > idx + 1 || (isLast && progress >= 99.5);
                                const isCurrent = !isDone && currentStep.step === idx + 1;
                                const position = isFirst
                                    ? "inset-s-0"
                                    : isLast
                                    ? "inset-e-0"
                                    : "-translate-x-1/2 rtl:translate-x-1/2";
                                return (
                                    <div
                                        key={idx}
                                        className={`absolute top-0 flex items-center gap-1 whitespace-nowrap transition-colors duration-300 ${position} ${
                                            scanStatus === "error"
                                                ? "text-red-400"
                                                : isDone
                                                ? "text-cyan-400"
                                                : isCurrent
                                                ? "text-cyan-200 font-semibold"
                                                : "text-gray-500"
                                        }`}
                                        style={isFirst || isLast ? undefined : { insetInlineStart: `${PROGRESS_STEP_THRESHOLDS[idx]}%` }}
                                        aria-current={isCurrent ? "step" : undefined}
                                    >
                                        {isDone && scanStatus !== "error" && <Check className="h-3 w-3" />}
                                        {isCurrent && isScanning && <span className="h-1.5 w-1.5 rounded-full bg-cyan-300 motion-safe:animate-pulse" />}
                                        {step}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

                    {/* Results/Error Section */}
                    {isScanning && (
                            <section className="w-full space-y-5 rounded-2xl border border-cyan-700/40 bg-linear-to-br from-slate-900/50 to-slate-800/50 backdrop-blur-xs p-5 shadow-lg shadow-cyan-500/5 animate-in fade-in-50 slide-in-from-bottom-3 duration-500" style={{contain: 'layout style paint'}}>
                        <div className="grid grid-cols-[minmax(0,1fr)] gap-5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:items-start">
                            <div className="flex flex-col gap-3 text-sm text-gray-400">
                                <p className="text-xs uppercase tracking-[0.12em] text-white">{messages.home.indicatorLabel}</p>
                                <div className="flex items-center gap-2 min-w-0">
                                    <div className="h-6 w-12 animate-skeleton-pulse rounded-full" />
                                    <div className="h-5 flex-1 animate-skeleton-pulse rounded" />
                                </div>
                            </div>
                            <div className="flex flex-col items-start gap-3 sm:items-end sm:text-right">
                                <p className="text-xs uppercase tracking-[0.12em] text-white">{messages.home.scanResultLabel}</p>
                                <div className="h-7 w-20 animate-skeleton-pulse rounded-full" />
                            </div>
                        </div>

                        <hr className="h-px border-0 bg-linear-to-r from-transparent via-cyan-700/50 to-transparent" />

                        <div className="grid grid-cols-[minmax(0,1fr)] gap-5 lg:grid-cols-2">
                            <div className="space-y-3 rounded-2xl border border-cyan-500/20 bg-slate-900/40 p-4" style={{contain: 'layout style paint'}}>
                                <div className="flex items-center justify-between">
                                    <div className="h-4 w-40 animate-skeleton-pulse rounded" />
                                    <div className="h-6 w-8 animate-skeleton-pulse rounded-full" />
                                </div>
                                <div className="space-y-3">
                                    {[...Array(2)].map((_, idx) => (
                                        <div key={idx} className="rounded-xl border border-red-600/40 bg-red-600/10 p-3 space-y-2">
                                            <div className="flex items-center gap-2">
                                                <div className="h-4 w-4 shrink-0 animate-skeleton-pulse rounded" />
                                                <div className="h-4 flex-1 animate-skeleton-pulse rounded" />
                                            </div>
                                            <div className="flex items-center gap-2 pl-6">
                                                <div className="h-5 w-12 animate-skeleton-pulse rounded-full" />
                                                <div className="h-4 flex-1 animate-skeleton-pulse rounded" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-3 rounded-2xl border border-cyan-500/20 bg-slate-900/40 p-4" style={{contain: 'layout style paint'}}>
                                <div className="flex items-center justify-between">
                                    <div className="h-4 w-32 animate-skeleton-pulse rounded" />
                                    <div className="h-6 w-8 animate-skeleton-pulse rounded-full" />
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {[...Array(5)].map((_, idx) => (
                                        <div key={idx} className="h-7 w-24 animate-skeleton-pulse rounded-full" />
                                    ))}
                                </div>
                            </div>
                        </div>
                        
                        <hr className="h-px border-0 bg-linear-to-r from-transparent via-cyan-700/50 to-transparent" />

                        {/* Alternative indicators skeleton: label rows with chips */}
                        <div className="flex flex-col gap-3">
                            <div className="h-3 w-40 animate-skeleton-pulse rounded" />
                            {[...Array(3)].map((_, idx) => (
                                <div key={idx} className="grid grid-cols-1 gap-2 sm:grid-cols-[150px_minmax(0,1fr)] sm:gap-4">
                                    <div className="h-3 w-28 animate-skeleton-pulse rounded" />
                                    <div className="flex flex-wrap gap-1.5">
                                        {[...Array(idx === 0 ? 3 : 2)].map((_, chipIdx) => (
                                            <div key={chipIdx} className="h-5 w-28 animate-skeleton-pulse rounded-md" />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <hr className="h-px border-0 bg-linear-to-r from-transparent via-cyan-700/50 to-transparent" />

                        <div className="flex justify-between">
                            <div className="h-3 w-32 animate-skeleton-pulse rounded" />
                            <div className="h-3 w-40 animate-skeleton-pulse rounded" />
                        </div>
                                    </section>
                        )
                    }

                    {
                        !isScanning && scanStatus === "success" && (
                            <section className="w-full space-y-5 rounded-2xl border border-cyan-700/40 bg-linear-to-br from-slate-900/50 to-slate-800/50 backdrop-blur-xs p-5 shadow-lg shadow-cyan-500/5 animate-in fade-in-50 slide-in-from-bottom-3 duration-500" style={{contain: 'layout style paint'}}>
                        <div className="grid grid-cols-[minmax(0,1fr)] gap-5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:items-start">
                            <div className="flex flex-col items-start gap-3 sm:items-end sm:text-right order-first sm:order-last">
                                <p className="text-xs uppercase tracking-[0.12em] text-white">{messages.home.scanResultLabel}</p>
                                <Badge variant={scanResult === null ? "secondary" : scanResult ? "destructive" : "success"} className={`text-sm font-semibold shadow-lg ${scanResult ? 'bg-red-700 text-white shadow-red-600/30 border-red-600/50' : 'bg-green-700 text-white shadow-green-600/30 border-green-600/50'}`}>
                                    {scanResult === null ? "Unknown" : scanResult ? "Phishing" : "Safe"}
                                </Badge>
                            </div>
                            <div className="flex min-w-0 flex-col gap-3 text-sm text-gray-400 order-last sm:order-first">
                                <p className="text-xs uppercase tracking-[0.12em] text-white">{messages.home.indicatorLabel}</p>
                                {scanType && (
                                    <div className="flex flex-wrap items-center gap-2 min-w-0">
                                        <Badge variant="outline" className="w-fit rounded-full border-cyan-500/50 bg-cyan-500/10 text-xs uppercase tracking-[0.12em] text-gray-300 shrink-0">
                                            {scanType}
                                        </Badge>
                                        <span className={`text-sm font-mono break-all min-w-0 cursor-pointer max-w-full transition-colors hover:text-cyan-200 hover:font-semibold ${originalTagged ? 'text-red-500 font-semibold' : 'text-gray-300'}`} title={`Click to copy ${window.__fileHashes ? inputValue : (normalizedIndicator || inputValue)}`} onClick={() => handleCopy(window.__fileHashes ? inputValue : (normalizedIndicator || inputValue))}>
                                            {window.__fileHashes ? inputValue : (normalizedIndicator || inputValue)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <hr className="h-px border-0 bg-linear-to-r from-transparent via-cyan-700/50 to-transparent" />

                        <div className="grid grid-cols-[minmax(0,1fr)] gap-5 lg:grid-cols-2">
                            <div className="space-y-3 rounded-2xl border border-cyan-700/40 bg-slate-900/40 p-4" style={{contain: 'layout style paint'}}>
                                <div className="flex items-center justify-between">
                                    <h3 className="text-xs uppercase tracking-[0.12em] text-white">{messages.home.detectionsLabel}</h3>
                                    <Badge variant="outline" className={`rounded-full ${matches.length ? 'border-red-600/50 bg-red-600/10 text-red-500' : 'border-cyan-500/50 bg-cyan-500/10 text-gray-300'}`}>
                                        {matches.length}
                                    </Badge>
                                </div>
                                {
                                    matches.length ? (
                                        <ul className="space-y-3 animate-in fade-in-50 duration-500">
                                            {
                                                matches.map((match, idx) => (
                                                    <li key={`${match.feed}-${match.source ?? "unknown"}-${idx}`} className="rounded-xl border border-red-600/40 bg-red-600/10 p-3 text-red-500 transition-transform duration-300 ease-out hover:-translate-y-0.5">
                                                        <div className="flex min-w-0 items-center gap-2 text-sm font-semibold">
                                                            <Bug className="h-4 w-4 shrink-0" />
                                                            <span className="min-w-0 truncate" title={match.feed}>{match.feed}</span>
                                                        </div>
                                                        {match.indicator && match.indicatorType && (
                                                            <div className="mt-2 flex min-w-0 flex-wrap items-center gap-2">
                                                                <Badge variant="outline" className="w-fit shrink-0 rounded-full border-red-600/40 bg-red-600/5 text-xs uppercase tracking-[0.12em] text-red-500">
                                                                    {match.indicatorType === "ip" 
                                                                        ? (/^\d{1,3}(\.\d{1,3}){3}$/.test(match.indicator || "") ? "IPv4" : "IPv6")
                                                                        : match.indicatorType
                                                                    }
                                                                </Badge>
                                                                <button
                                                                    type="button"
                                                                    className="min-w-0 max-w-full break-all text-left text-xs text-red-300/80 font-mono cursor-pointer transition-colors hover:text-cyan-200 hover:font-semibold"
                                                                    title={`Click to copy ${match.indicator}`}
                                                                    onClick={() => handleCopy(match.indicator!)}
                                                                >{match.indicator}</button>
                                                            </div>
                                                        )}
                                                    </li>
                                                ))
                                            }
                                        </ul>
                                    ) : (
                                        <p className="text-sm text-gray-400">No detections reported.</p>
                                    )
                                }
                            </div>

                            <div className="space-y-3 rounded-2xl border border-purple-500/30 bg-slate-900/40 p-4" style={{contain: 'layout style paint'}}>
                                <div className="flex items-center justify-between">
                                    <h3 className="text-xs uppercase tracking-[0.12em] text-white">{messages.home.sourcesLabel}</h3>
                                    <Badge variant="outline" className={`rounded-full ${flaggedCount ? 'border-red-600/50 bg-red-600/10 text-red-500' : 'border-cyan-500/50 bg-cyan-500/10 text-gray-300'}`}>
                                        {flaggedCount}
                                    </Badge>
                                </div>
                                {
                                    feedSummary && feedSummary.statuses.length ? (
                                        (() => {
                                            const sortedFeeds = [...feedSummary.statuses]
                                                .sort((a, b) => {
                                                    if (!!b.matched === !!a.matched) {
                                                        return a.name.localeCompare(b.name);
                                                    }
                                                    return b.matched ? 1 : -1;
                                                });
                                            const displayCount = 5;
                                            const feedsToShow = showAllSources ? sortedFeeds : sortedFeeds.slice(0, displayCount);
                                            return (
                                                <>
                                                    <ul className="flex flex-wrap gap-2 animate-in fade-in-50 duration-500">
                                                        {feedsToShow.map((feed) => {
                                                            const isMatched = !!feed.matched;
                                                            const info = getFeedStatusStyles(feed.status, isMatched);
                                                            const tooltip = feed.name;
                                                            let StatusIcon;
                                                            let iconColor;
                                                            if (isMatched) {
                                                                StatusIcon = Bug;
                                                                iconColor = 'text-red-600';
                                                            } else {
                                                                StatusIcon = Check;
                                                                iconColor = 'text-green-500';
                                                            }
                                                            return (
                                                                <li key={feed.name}>
                                                                    <span
                                                                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${info.classes}`}
                                                                        title={tooltip}
                                                                    >
                                                                        <StatusIcon className={`h-3 w-3 shrink-0 ${iconColor}`} />
                                                                        <span>{feed.name}</span>
                                                                    </span>
                                                                </li>
                                                            );
                                                        })}
                                                        {!showAllSources && sortedFeeds.length > displayCount && (
                                                            <li>
                                                                <button
                                                                    className="inline-flex items-center gap-2 rounded-full border border-cyan-500/50 bg-cyan-500/10 px-3 py-1 text-xs font-medium text-cyan-400 hover:bg-cyan-500/20 transition-colors"
                                                                    onClick={() => setShowAllSources(true)}
                                                                >
                                                                    ...more
                                                                </button>
                                                            </li>
                                                        )}
                                                        {showAllSources && sortedFeeds.length > displayCount && (
                                                            <li>
                                                                <button
                                                                    className="inline-flex items-center gap-2 rounded-full border border-cyan-500/50 bg-cyan-500/10 px-3 py-1 text-xs font-medium text-cyan-400 hover:bg-cyan-500/20 transition-colors"
                                                                    onClick={() => setShowAllSources(false)}
                                                                >
                                                                    show less
                                                                </button>
                                                            </li>
                                                        )}
                                                    </ul>
                                                </>
                                            );
                                        })()
                                    ) : (
                                        <p className="text-sm text-gray-400">Source status unavailable.</p>
                                    )
                                }
                            </div>
                        </div>
                        {(alternativeIndicators.length > 0 || mxMissing.length > 0) && (
                            <>
                                <hr className="h-px border-0 bg-linear-to-r from-transparent via-cyan-700/50 to-transparent" />
                                {/* Pivots checked alongside the indicator: one row per record type, values as copyable chips */}
                                <div className="flex flex-col gap-3">
                                    <h3 className="text-xs uppercase tracking-[0.12em] text-white">{messages.home.pivotsLabel}</h3>
                                    <dl className="grid grid-cols-1 gap-x-4 gap-y-2.5 sm:grid-cols-[150px_minmax(0,1fr)] sm:items-start">
                                        {alternativeIndicators.map((alt) => (
                                            <React.Fragment key={alt.label}>
                                                <dt className="text-[11px] uppercase tracking-widest text-gray-400 sm:pt-1">
                                                    {alt.label.replace(/\s*\(([^)]*)\)\s*$/, " · $1")}
                                                </dt>
                                                <dd className="flex min-w-0 flex-wrap gap-1.5">
                                                    {alt.indicators.map((indicator) => (
                                                        <button
                                                            type="button"
                                                            key={indicator.value}
                                                            onClick={() => handleCopy(indicator.value)}
                                                            title={`Click to copy ${indicator.value}`}
                                                            className={`max-w-full break-all rounded-md border px-2 py-0.5 text-left font-mono text-xs transition-colors ${
                                                                indicator.tagged
                                                                    ? "border-red-600/50 bg-red-600/10 font-semibold text-red-500 hover:border-red-500"
                                                                    : "border-slate-700/70 bg-slate-950/50 text-gray-300 hover:border-cyan-500/60 hover:text-cyan-200"
                                                            }`}
                                                        >
                                                            {indicator.value}
                                                        </button>
                                                    ))}
                                                </dd>
                                            </React.Fragment>
                                        ))}
                                        {mxMissing.length > 0 && (
                                            <>
                                                <dt className="text-[11px] uppercase tracking-widest text-gray-400 sm:pt-1">Domain · MX Record</dt>
                                                <dd className="flex flex-col gap-1.5">
                                                    {mxMissing.map((domain) => (
                                                        <span key={domain} className="inline-flex items-center gap-2 text-sm text-gray-400">
                                                            <MailX className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
                                                            <span>
                                                                No mail server published for <span className="font-mono break-all text-gray-300">{domain}</span>
                                                            </span>
                                                        </span>
                                                    ))}
                                                </dd>
                                            </>
                                        )}
                                    </dl>
                                </div>
                            </>
                        )}

                        <hr className="h-px border-0 bg-linear-to-r from-transparent via-cyan-700/50 to-transparent" />

                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 text-xs text-gray-500">
                            <p>Processing Time · {formattedProcessingTime ?? "0ms"}</p>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                {/* Share Buttons */}
                                {(() => {
                                    const shareIndicator = normalizedIndicator || inputValue;
                                    const shareResult = scanResult ? "Phishing" : "Safe";
                                    // Defang URL for title only
                                    let defangedIndicator = shareIndicator;
                                    if (/^https?:\/\//i.test(defangedIndicator)) {
                                        defangedIndicator = defangedIndicator
                                            .replace(/^https?:\/\//i, (match) => match.replace(':', '[:]'))
                                            .replace(/\./g, '[.]');
                                    }
                                    const shareTitle = `I just scanned "${defangedIndicator}" on ScaMatrix - Result: ${shareResult} 🔍`;
                                    const shareSubject = 'ScaMatrix - AI Powered Threat Intelligence Lookup';
                                    const shareUrlObject = new URL(buildAbsoluteUrl("/"));
                                    shareUrlObject.searchParams.set("scan", shareIndicator);
                                    const shareUrl = shareUrlObject.toString();
                                    return (
                                        <div className="flex items-center gap-2 pl-3 border-l border-gray-600">
                                            <TwitterShareButton
                                                url={shareUrl}
                                                title={shareTitle}
                                            >
                                                <TwitterIcon size={24} round />
                                            </TwitterShareButton>
                                            <LinkedinShareButton
                                                url={shareUrl}
                                                title={shareSubject}
                                                summary={shareTitle}
                                            >
                                                <LinkedinIcon size={24} round />
                                            </LinkedinShareButton>
                                            <TelegramShareButton
                                                url={shareUrl}
                                                title={shareTitle}
                                            >
                                                <TelegramIcon size={24} round />
                                            </TelegramShareButton>
                                            <EmailShareButton
                                                url={shareUrl}
                                                subject={shareSubject}
                                                body={shareTitle}
                                            >
                                                <EmailIcon size={24} round />
                                            </EmailShareButton>
                                            <WhatsappShareButton
                                                url={shareUrl}
                                                title={shareTitle}
                                            >
                                                <WhatsappIcon size={24} round />
                                            </WhatsappShareButton>
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    </section>
                    )
                }

                    {/* Idle state: explain the product with an illustrated example result */}
                    {!isResultsMode && (
                        <ScanShowcase showcase={messages.home.showcase} onCta={focusScanInput} />
                    )}

                    {
                        !isScanning && scanStatus === "error" && errorMessage && isBusy && (
                            <section className="w-full flex flex-col gap-4 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-5 shadow-lg shadow-amber-500/10 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex gap-3">
                                    <Clock className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
                                    <div>
                                        <h3 className="text-base font-semibold text-amber-200">{messages.home.busy.title}</h3>
                                        <p className="mt-1 text-sm leading-relaxed text-amber-100/80">{messages.home.busy.body}</p>
                                    </div>
                                </div>
                                <a
                                    href={`${buildLocalizedPath("/developers", lang)}#commercial`}
                                    className="inline-flex shrink-0 items-center justify-center rounded-full bg-linear-to-r from-cyan-500 to-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-cyan-500/30 transition-all hover:from-cyan-400 hover:to-blue-500"
                                >
                                    {messages.home.busy.cta}
                                </a>
                            </section>
                        )
                    }

                    {
                        !isScanning && scanStatus === "error" && errorMessage && !isBusy && (
                            <section className="w-full rounded-2xl border border-red-600/40 bg-red-600/10 p-5 text-red-500 shadow-lg shadow-red-600/10">
                                <h3 className="text-base font-semibold">Unable to complete scan</h3>
                                <p className="mt-2 text-sm text-red-400/80">{errorMessage}</p>
                            </section>
                        )
                    }
                </div>
            </div>
        </div>
    );
}
